import { useEffect, useRef } from "react";
import { useAuth } from "../context/AuthContext";

const MIN_DISPLAY_MS = 2500;
const MAX_WAIT_MS = 3500;

// Module-level (not React state) — the static #app-splash node only ever
// needs to be hidden ONCE per real page load. If auth `loading` flips back
// to true later (e.g. logout → login), this guard stops it from trying to
// show/hide again.
let splashHidden = false;

function hideSplash() {
  if (splashHidden) return;
  splashHidden = true;
  const el = document.getElementById("app-splash");
  if (el) {
    el.classList.add("app-splash-hide");
    setTimeout(() => el.remove(), 300);
  }
  // Lets anything gated on "the launch splash is gone" (e.g. the Home page's
  // auto-geolocation prompt) know it's now safe to run.
  window.dispatchEvent(new Event("infocity:splash-hidden"));
}

// Covers the real app.init sequence (auth/session check) — fades out once
// that's done AND at least MIN_DISPLAY_MS has passed, whichever is later,
// capped by MAX_WAIT_MS as a safety net so a stuck request never leaves a
// blank app behind the splash forever.
export function useHideSplashScreen() {
  const { loading } = useAuth();
  const mountedAtRef = useRef(Date.now());

  useEffect(() => {
    const maxTimer = setTimeout(hideSplash, MAX_WAIT_MS);
    return () => clearTimeout(maxTimer);
  }, []);

  useEffect(() => {
    if (splashHidden || loading) return;
    const remaining = MIN_DISPLAY_MS - (Date.now() - mountedAtRef.current);
    const t = setTimeout(hideSplash, Math.max(0, remaining));
    return () => clearTimeout(t);
  }, [loading]);
}
